import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowLeft, Trophy, BarChart3, Clock } from 'lucide-react'
import { useApp } from '../contexts/AppContext'
import Button from '../components/ui/Button'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import TicTacToeGame from '../components/games/TicTacToe/TicTacToeGame'

export default function TicTacToePage() {
  const { getRecentResults } = useApp()
  const recentResults = getRecentResults('tic-tac-toe', 5)
  
  const stats = {
    totalGames: recentResults.length,
    xWins: recentResults.filter(r => r.winner === 'X').length,
    oWins: recentResults.filter(r => r.winner === 'O').length,
    draws: recentResults.filter(r => !r.winner).length,
    averageMoves: recentResults.length > 0 
      ? Math.round(recentResults.reduce((sum, r) => sum + (r.moves || 0), 0) / recentResults.length)
      : 0
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button
              as={Link}
              to="/"
              variant="ghost"
              size="sm"
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <h1 className="text-4xl font-bold">Tic Tac Toe</h1>
            <p className="text-text-muted mt-2">
              Classic 3x3 grid game. Play against the computer or a friend.
            </p>
          </div>
        </div>
      </motion.div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Game Area - 2/3 width */}
        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <TicTacToeGame />
          </motion.div>
        </div>
        
        {/* Stats Sidebar - 1/3 width */}
        <div className="space-y-6">
          {/* Game Stats */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card shadow="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-bold">Game Statistics</h2>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Total Games</span>
                    <span className="font-semibold">{stats.totalGames}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">X Wins</span>
                    <span className="font-semibold text-primary">{stats.xWins}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">O Wins</span>
                    <span className="font-semibold text-secondary">{stats.oWins}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Draws</span>
                    <span className="font-semibold">{stats.draws}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Avg Moves</span>
                    <span className="font-semibold">{stats.averageMoves}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          {/* Recent Games */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card shadow="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-bold">Recent Games</h2>
                </div>
              </CardHeader>
              <CardContent>
                {recentResults.length > 0 ? (
                  <div className="space-y-3">
                    {recentResults.map((result, index) => (
                      <div 
                        key={result.id || index}
                        className="flex items-center justify-between p-3 rounded-lg bg-surface-variant"
                      >
                        <div className="flex items-center gap-3">
                          <Trophy className="w-4 h-4 text-yellow-500" />
                          <div>
                            <div className="font-medium">
                              {result.winner ? `${result.winner} won` : 'Draw'}
                            </div>
                            <div className="text-sm text-text-muted">
                              {result.moves} moves • {result.duration}s
                            </div>
                          </div>
                        </div>
                        <div className="font-bold">
                          {result.score}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-text-muted">
                    <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No games played yet</p>
                    <p className="text-sm mt-1">Play a game to see your results here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
          
          {/* How to Play */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Card shadow="md">
              <CardHeader>
                <h2 className="text-xl font-bold">How to Play</h2>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-text-muted">
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">1.</span>
                    <span>Players take turns placing X and O marks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">2.</span>
                    <span>First to get 3 in a row (horizontal, vertical, or diagonal) wins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">3.</span>
                    <span>If all squares are filled without a winner, it's a draw</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary font-bold">4.</span>
                    <span>Click any empty cell to make your move</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}